import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Layers, Users, Building2, Brain, Cog, Code, 
  ChevronRight, Check, ArrowRight, ChevronDown 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Layers: Layers,
  Users: Users,
  Building2: Building2,
  Brain: Brain,
  Cog: Cog,
  Code: Code,
};

// JSON-LD Schema for Service
const ServiceSchema = ({ service }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": "Plutops",
      "url": window.location.origin
    },
    "serviceType": service.title,
    "areaServed": "Worldwide"
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// FAQ Schema
const FAQSchema = ({ faq }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API}/services/${slug}`);
        setService(response.data);
        document.title = `${response.data.title} | Plutops`;
      } catch (error) {
        console.error('Failed to fetch service:', error);
        setError('Service not found');
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-heading font-bold text-text-primary mb-4">Service Not Found</h1>
        <Link to="/services">
          <Button className="bg-primary text-white rounded-full">
            View All Services
          </Button>
        </Link>
      </div>
    );
  }

  const Icon = iconMap[service.icon] || Layers;

  return (
    <div data-testid="service-detail-page">
      {/* Schema Markup */}
      <ServiceSchema service={service} />
      {service.faq && <FAQSchema faq={service.faq} />}

      {/* Breadcrumb */}
      <div className="bg-warm-50 py-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm text-text-muted">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-text-primary">{service.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Icon className="h-8 w-8 text-primary" />
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
                {service.title}
              </h1>
              <p className="text-lg text-text-secondary mb-8">
                {service.description}
              </p>
              <Link to="/contact">
                <Button
                  data-testid="service-cta-btn"
                  className="bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-6 text-lg"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl shadow-lg p-8 border border-slate-100"
            >
              <h3 className="font-heading font-bold text-xl text-text-primary mb-6">Key Features</h3>
              <ul className="space-y-4">
                {service.features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    className="flex items-start"
                  >
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-primary" />
                    </div>
                    <span className="ml-3 text-text-secondary">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Long Description */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-6">Overview</h2>
            <div className="text-text-secondary whitespace-pre-line leading-relaxed">
              {service.long_description}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">Benefits</h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Why choose our {service.title.toLowerCase()} service
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
              >
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <p className="text-text-secondary">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      {service.comparison_table && service.comparison_table.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">Comparison</h2>
              <p className="text-text-secondary">See how we stack up</p>
            </motion.div>

            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold text-text-primary">Aspect</TableHead>
                    <TableHead className="font-heading font-bold text-text-primary">Traditional</TableHead>
                    <TableHead className="font-heading font-bold text-primary">Our Solution</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {service.comparison_table.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{row.aspect}</TableCell>
                      <TableCell className="text-text-secondary">{row.in_house || row.traditional}</TableCell>
                      <TableCell className="text-primary font-medium">{row.our_solution || row.augmentation}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section className="py-16 lg:py-24 bg-slate-50">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <Accordion type="single" collapsible className="space-y-4">
              {service.faq.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-white rounded-xl border border-slate-100 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-heading font-medium text-text-primary hover:text-primary py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-text-secondary pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Let's discuss how our {service.title.toLowerCase()} service can help transform your business.
            </p>
            <Link to="/contact">
              <Button
                data-testid="service-bottom-cta-btn"
                className="bg-white text-primary hover:bg-warm-100 rounded-full px-8 py-6 text-lg"
              >
                Schedule a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
